import clsx from 'clsx';
import {
  AdjustmentsHorizontalIconOutline,
  BookmarkSquareIconOutline,
  CircleStackIconOutline,
  MagnifyingGlassIconOutline,
  StarIconOutline,
  ViewColumnsIconOutline,
} from './rightAsideSidebarViewIcons';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import SharedButton from '@/global/Button/Button';
import { NAVBAR_HEIGHT_PX, LAYOUT_HEADER_OFFSET_CSS_VAR } from '@/constants/layoutHeaderInset';

const desktopHeaderOffset = `var(${LAYOUT_HEADER_OFFSET_CSS_VAR}, ${NAVBAR_HEIGHT_PX}px)`;
import { SidebarSection } from './SidebarSection';
import { SidebarSources } from './SidebarSources';
import { SidebarColumns } from './SidebarColumns';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';

interface CollapsedSection {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
  headerBadge?: string | number;
  content: React.ReactNode;
}

interface RightAsideSidebarViewProps {
  isOpen: boolean;
  isDesktop: boolean;
  sidebarWidth: string | number;
  mobileClosedWidth: string;
  desktopCollapsedWidth: string;
  isDesktopCollapsed: boolean;
  isMobileLayout: boolean;
  setSidebarOpen: (open: boolean) => void;
  onClose: () => void;
  closeButtonRef: React.RefObject<HTMLButtonElement>;
  searchItemsSection: React.ReactNode;
  showSearchItemsSection: boolean;
  searchItemsCount: number;
  searchItemsSectionExpandSignal: number;
  showMyScoreSection: boolean;
  tab: string;
  onOpenScoreModal?: () => void;
  onApplySavedScore?: (score: any) => void;
  onClearWeights?: () => void;
  myScoreInlineContent: React.ReactNode;
  sourcesHeaderBadge?: string | number;
  isAuthenticated: boolean;
  view: string;
  navigate: (path: string) => void;
  source: string;
  sourceWeights: Array<{ name: string; score: number }>;
  columnVisibilityModel: Record<string, boolean>;
  columnPanelToggle?: { active: 'summary' | 'evidence'; onChange: (active: 'summary' | 'evidence') => void };
  shouldShowResetButton: () => boolean;
  onResetToDefault?: () => void;
  effectiveSidebarTab: string;
  isEvidence: boolean;
  setColumnVisibilityModel: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  columnOrder?: string[];
  setColumnOrder?: (order: string[]) => void;
  favoritesSection: React.ReactNode;
  favoritesCount: number;
  savedColumnsSection: React.ReactNode;
  savedColumnsCount: number;
  collapsedSections: CollapsedSection[];
  activeCollapsedPanel: string | null;
  pinnedCollapsedPanel: string | null;
  clearCollapsedHoverCloseTimeout: () => void;
  scheduleCollapsedFlyoutClose: (sectionId: string) => void;
  setActiveCollapsedPanel: (value: string | null) => void;
  setPinnedCollapsedPanel: (value: string | null) => void;
  sidebarFlyoutWidthPx: number;
}

export function RightAsideSidebarView({
  isOpen,
  isDesktop,
  sidebarWidth,
  mobileClosedWidth,
  desktopCollapsedWidth,
  isDesktopCollapsed,
  isMobileLayout,
  setSidebarOpen,
  onClose,
  closeButtonRef,
  searchItemsSection,
  showSearchItemsSection,
  searchItemsCount,
  searchItemsSectionExpandSignal,
  showMyScoreSection,
  tab,
  onOpenScoreModal,
  onApplySavedScore,
  onClearWeights,
  myScoreInlineContent,
  sourcesHeaderBadge,
  isAuthenticated,
  view,
  navigate,
  source,
  sourceWeights,
  columnVisibilityModel,
  columnPanelToggle,
  shouldShowResetButton,
  onResetToDefault,
  effectiveSidebarTab,
  isEvidence,
  setColumnVisibilityModel,
  columnOrder,
  setColumnOrder,
  favoritesSection,
  favoritesCount,
  savedColumnsSection,
  savedColumnsCount,
  collapsedSections,
  activeCollapsedPanel,
  pinnedCollapsedPanel,
  clearCollapsedHoverCloseTimeout,
  scheduleCollapsedFlyoutClose,
  setActiveCollapsedPanel,
  setPinnedCollapsedPanel,
  sidebarFlyoutWidthPx,
}: RightAsideSidebarViewProps) {
  return (
    <aside
      role="complementary"
      aria-label="Table controls"
      className={clsx(
        'fixed right-0 z-40 flex flex-col border-l border-gray-200 bg-white shadow-lg transition-[width] duration-300 ease-out',
        isDesktopCollapsed ? 'overflow-visible' : 'overflow-hidden',
      )}
      style={{
        top: isDesktop ? desktopHeaderOffset : 'auto',
        bottom: !isDesktop ? '0' : 'auto',
        height: isDesktop ? `calc(100vh - ${desktopHeaderOffset})` : `calc(100vh - ${NAVBAR_HEIGHT_PX}px)`,
        width: isOpen ? sidebarWidth : isMobileLayout ? mobileClosedWidth : desktopCollapsedWidth,
        minWidth: isOpen ? sidebarWidth : isMobileLayout ? mobileClosedWidth : desktopCollapsedWidth,
        maxWidth: isOpen ? sidebarWidth : isMobileLayout ? mobileClosedWidth : desktopCollapsedWidth,
      }}
    >
      {isMobileLayout && (
        <button
          type="button"
          onPointerDown={e => {
            e.preventDefault();
            e.stopPropagation();
            if (isOpen) {
              setSidebarOpen(false);
              onClose();
              return;
            }
            setSidebarOpen(true);
          }}
          className="absolute left-0 top-0 z-50 flex h-full w-4 items-center justify-center border-r border-gray-300 bg-gray-200/95 text-gray-600 hover:bg-gray-300 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-l-md"
          aria-label={isOpen ? 'Close sidebar' : 'Open sidebar'}
          title={isOpen ? 'Close controls' : 'Open controls'}
        >
          {isOpen ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronLeftIcon className="h-4 w-4" />}
        </button>
      )}

      {isOpen && (
        <>
          <div
            className={clsx(
              'flex items-center justify-between flex-shrink-0 py-3 border-b border-gray-200 bg-gray-50 min-w-0',
              isMobileLayout ? 'pl-7 pr-4' : 'px-4',
            )}
          >
            <h2 className="text-sm font-semibold truncate min-w-0 mb-0">Table controls</h2>
            {!isMobileLayout && (
              <button
                ref={closeButtonRef}
                type="button"
                onPointerDownCapture={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSidebarOpen(false);
                  onClose();
                }}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 flex-shrink-0"
                aria-label="Close sidebar"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          <div
            className="flex-1 flex flex-col min-h-0 overflow-y-auto overflow-x-hidden min-w-0"
            style={{ width: sidebarWidth }}
          >
            {showSearchItemsSection && (
              <SidebarSection
                title="Search items"
                prefixIcon={<MagnifyingGlassIconOutline className="h-4 w-4 text-pink" />}
                className="min-h-0 flex-shrink-0"
                defaultOpen={searchItemsCount > 0}
                expandSignal={searchItemsSectionExpandSignal}
                headerBadge={searchItemsCount > 0 ? searchItemsCount : undefined}
              >
                {searchItemsSection}
              </SidebarSection>
            )}

            {showMyScoreSection && tab && onOpenScoreModal && onApplySavedScore && onClearWeights && (
              <SidebarSection
                title="My Score"
                prefixIcon={<AdjustmentsHorizontalIconOutline className="h-4 w-4 text-pink" />}
                className="min-h-0 flex-shrink-0"
              >
                {myScoreInlineContent}
              </SidebarSection>
            )}

            <SidebarSection
              title="Sources"
              prefixIcon={<CircleStackIconOutline className="h-4 w-4 text-pink" />}
              headerBadge={sourcesHeaderBadge}
              className="min-h-[140px] max-h-[200px]"
              lockedOverlay={
                !isAuthenticated && view === 'ALL' ? (
                  <div className="flex flex-col items-center gap-1.5 p-4 text-center bg-white rounded border">
                    <span className="text-xs font-medium text-gray-600">Sign in to customize sources</span>
                    <SharedButton size="xs" variant="secondary" onClick={() => navigate('/login')}>
                      Sign in
                    </SharedButton>
                  </div>
                ) : undefined
              }
            >
              <SidebarSources tab={tab} source={source} sourceWeights={sourceWeights} />
            </SidebarSection>

            <SidebarSection
              title="Columns"
              prefixIcon={<ViewColumnsIconOutline className="h-4 w-4 text-pink" />}
              headerBadge={Object.values(columnVisibilityModel || {}).filter(Boolean).length}
              className="min-h-[160px] max-h-[280px]"
              lockedOverlay={
                !isAuthenticated && view === 'ALL' ? (
                  <div className="flex flex-col items-center gap-1.5 p-4 text-center bg-white rounded border">
                    <span className="text-xs font-medium text-gray-600">Sign in to customize columns</span>
                    <SharedButton size="xs" variant="secondary" onClick={() => navigate('/login')}>
                      Sign in
                    </SharedButton>
                  </div>
                ) : undefined
              }
            >
              {columnPanelToggle && (
                <div className="flex gap-2">
                  <div className="flex px-3 py-2 ">
                    <SharedButton
                      onClick={() => columnPanelToggle.onChange('summary')}
                      variant={columnPanelToggle.active === 'summary' ? 'secondary' : 'terciary'}
                      size="xxs"
                      weight="md"
                      className="ml-0 mt-0 mr-1"
                    >
                      Summary
                    </SharedButton>
                    <SharedButton
                      onClick={() => columnPanelToggle.onChange('evidence')}
                      variant={columnPanelToggle.active === 'evidence' ? 'secondary' : 'terciary'}
                      size="xxs"
                      weight="md"
                      className="mr-0 ml-1 mt-0"
                    >
                      Evidence
                    </SharedButton>
                  </div>
                  {shouldShowResetButton() && (
                    <BaseTooltip content="Reset columns to default view" position="bottom">
                      <SharedButton
                        onClick={onResetToDefault}
                        className="mt-2"
                        variant="secondaryText"
                        size="xs"
                      >
                        Reset Columns
                      </SharedButton>
                    </BaseTooltip>
                  )}
                </div>
              )}
              <SidebarColumns
                view={view}
                tab={effectiveSidebarTab}
                source={source}
                isEvidence={isEvidence}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                columnOrder={columnOrder}
                setColumnOrder={setColumnOrder}
              />
            </SidebarSection>

            <SidebarSection
              title="Favorites"
              prefixIcon={<StarIconOutline className="h-4 w-4 text-pink" />}
              headerBadge={favoritesCount > 0 ? favoritesCount : undefined}
              className="flex-shrink-0"
              growWithContent
            >
              {favoritesSection}
            </SidebarSection>

            <SidebarSection
              title="Saved Columns"
              prefixIcon={<BookmarkSquareIconOutline className="h-4 w-4 text-pink" />}
              headerBadge={savedColumnsCount > 0 ? savedColumnsCount : undefined}
              className="min-h-0 flex-shrink-0"
            >
              {shouldShowResetButton() && (
                <div className="flex justify-end">
                  <BaseTooltip content="Reset columns to default view" position="bottom">
                    <SharedButton
                      onClick={onResetToDefault}
                      className="mt-2"
                      variant="secondaryText"
                      size="xs"
                    >
                      Reset Columns
                    </SharedButton>
                  </BaseTooltip>
                </div>
              )}
              {savedColumnsSection}
            </SidebarSection>
          </div>
        </>
      )}

      {isDesktopCollapsed && (
        <div className="relative flex h-full w-full flex-col items-center bg-white py-2">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="mb-2 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 shadow-sm hover:text-gray-800 hover:border-gray-300"
            aria-label="Open controls"
            title="Open controls"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>

          <div className="flex h-full w-full flex-col items-center gap-1 overflow-visible px-1.5">
            {collapsedSections.map(section => {
              const Icon = section.icon;
              const isActive = activeCollapsedPanel === section.id || pinnedCollapsedPanel === section.id;
              const isPinned = pinnedCollapsedPanel === section.id;
              return (
                <div
                  key={section.id}
                  className="relative"
                  onMouseEnter={() => {
                    clearCollapsedHoverCloseTimeout();
                    setActiveCollapsedPanel(section.id);
                  }}
                  onMouseLeave={() => scheduleCollapsedFlyoutClose(section.id)}
                >
                  <button
                    type="button"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D98ABA]"
                    aria-label={section.title}
                    title={section.title}
                    style={{
                      color: isActive ? '#be0080' : '#6b7180',
                      backgroundColor: isActive ? 'rgba(236, 42, 153, 0.08)' : 'transparent',
                    }}
                  >
                    <Icon className="h-5 w-5" />
                  </button>

                  {isActive && (
                    <div
                      className="absolute right-full top-0 z-[200]"
                      onMouseEnter={clearCollapsedHoverCloseTimeout}
                      onMouseLeave={() => scheduleCollapsedFlyoutClose(section.id)}
                    >
                      <div
                        className={clsx(
                          'max-h-[70vh] rounded-2xl border border-gray-200 bg-white shadow-2xl',
                          section.id === 'my-score' ? 'overflow-visible' : 'overflow-hidden',
                        )}
                        style={{ width: `${sidebarFlyoutWidthPx}px` }}
                      >
                        <div className="border-b border-gray-100 px-4 py-2.5 flex items-center justify-between gap-2">
                          <p className="m-0 text-xs font-semibold uppercase tracking-wider text-gray-500">
                            {section.title}
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              if (isPinned) {
                                setPinnedCollapsedPanel(null);
                                return;
                              }
                              setPinnedCollapsedPanel(section.id);
                              setActiveCollapsedPanel(section.id);
                            }}
                            className={clsx(
                              'inline-flex items-center justify-center rounded-md p-1 transition-colors',
                              isPinned ? 'bg-[#FFE2F2] text-[#be0080]' : 'text-gray-400 hover:bg-gray-100',
                            )}
                            title={isPinned ? 'Unpin panel' : 'Pin panel'}
                            aria-label={isPinned ? 'Unpin panel' : 'Pin panel'}
                          >
                            <img
                              src={
                                isPinned
                                  ? '/static/images/icons/unpinIcon.svg'
                                  : '/static/images/icons/pinIcon.svg'
                              }
                              alt={isPinned ? 'Unpin' : 'Pin'}
                              className="h-4 w-4"
                            />
                          </button>
                        </div>
                        {section.id === 'columns' || section.id === 'sources' ? (
                          <div
                            className="overflow-hidden p-2"
                            style={{ height: 'min(420px, calc(70vh - 40px))' }}
                          >
                            <div className="h-full min-h-0 overflow-y-auto">{section.content}</div>
                          </div>
                        ) : section.id === 'my-score' ? (
                          <div className="p-2 overflow-visible">{section.content}</div>
                        ) : (
                          <div className="max-h-[calc(70vh-40px)] overflow-y-auto p-2">{section.content}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
}
